<template>
  <div class="confirmation-end-protocol">
    <Dialog width="740px" :canCloseManually="false" :showCloseIcon="true">
      <template slot="title">
        <h1 class="title">{{ modalConfirmationEndProtocol.title }}</h1>
      </template>
      <template slot="body">
        <div class="content">
          <div v-if="currentModal === 'End Protocol Early / Adjust Protocol'" class="protocol-adjustment-view">
            <div class="scrollable-area">
              <h2 class="content__title">What would you like to do with the Evaluation?</h2>
              <p class="content__description">Click on your choice and confirm</p>
              <div class="wrapper-protocol">
                <div
                  class="protocol protocol-adjust"
                  :class="{ active: form.activeProtocol === 'adjust-protocol' }"
                  @click="handleAction('adjust-protocol')">
                <span class="protocol__icon">
                  <TrackProtocolIcon/>
                </span>
                  <h3 class="protocol__title">Adjust Protocol</h3>
                  <div style="position: relative">
                    Adjusting Protocol will close this evaluation, placing it into a Closed status, and create a new
                    evaluation with an In Progress status.
                    <v-tooltip attach="parent" :open-on-hover="false" v-model="showInfoAdjustProtocol">
                      <template v-slot:activator="{}">
                        <span style="color: #008C89" @click.stop="onShowInfoAdjustProtocol">More...</span>
                      </template>
                      <template v-slot:default>
                        <div class="custom-tooltip">
                          <p>
                            Adjusting Protocol will close this evaluation, placing it into a Closed status, and create a
                            new evaluation with an In Progress status. Protocol supplements, patient notes and evaluation
                            notes will be duplicated into the new evaluation for your convenience. However, all system
                            generated reminders will be reset. Furthermore, since protocols are based on day's supply, if
                            your patient has already begun utilizing this protocol, any changes may result in the
                            shortages or overages of product for your patient.
                          </p>
                        </div>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
                <div
                  class="protocol protocol-end"
                  :class="{ active: form.activeProtocol === 'end-protocol' }"
                  @click="handleAction('end-protocol')">
                <span class="protocol__icon">
                  <EndEvaluationIcon/>
                </span>
                  <h3 class="protocol__title">End Protocol</h3>
                  <p>Choose this option if you simply want to end this current protocol. This evaluation will change to a
                    Closed status.</p>
                </div>
              </div>
              <div v-if="modalConfirmationEndProtocol.data.addVisit && !isAddNextEvaluationDisabled">
                <div class="custom-checkbox">
                  <v-checkbox
                    color="#008C89"
                    label="Add Next Evaluation Date"
                    @click="toggleNextEvaluation"
                    v-model="form.addNextEvaluation"
                  ></v-checkbox>
                </div>
                <transition name="fade">
                  <div :class="['elements-wrapper', form.addNextEvaluation ? 'visible' : 'hidden']">
                    <div class="elements">
                      <div class="evaluation-date mr-7">
                        <h3 class="elements__title">Evaluation Date</h3>
                        <div class="evaluation-date__wrapper">
                          <datepicker
                            ref="evaluationDatepicker"
                            :show-icon="false"
                            :min="nowDate"
                            v-model="form.evaluationDate"
                            @date:update="form.evaluationDate = $event"
                          />
                          <DateIcon
                            style="position: absolute; right: 13px; top: 10px; cursor: pointer; z-index: 3;"
                            @click="openEvaluationDatePicker"
                          />
                        </div>
                      </div>
                      <div class="evaluation-type">
                        <h3 class="elements__title">Select the Evaluation Type</h3>
                        <div class="elements__survey">
                          <v-checkbox
                            color="#008C89"
                            v-model="form.survey"
                            class="round-checkbox"
                            label="Survey"/>
                          <v-checkbox
                            color="#008C89"
                            v-model="form.nonSurvey"
                            class="round-checkbox"
                            label="Non-Survey"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>

          <div v-else-if="currentModal === 'Track Protocol / End Evaluation'" class="evaluation-finalization-view">
            <div class="consult-info-card">
              <div class="consult-info-card__left">
                <h3 class="consult-info-card__label">Date of Patient Consult</h3>
                <div class="consult-info-card__input-wrapper">
                  <div class="consult-info-card__date">
                    <datepicker
                      ref="consultDatepicker"
                      class="consult-datepicker-white"
                      :readonly="false"
                      :show-icon="false"
                      :max="todayDate"
                      v-model="form.consultDate"
                      @date:update="onConsultDateUpdate"
                    />

                    <DateIcon
                      style="position: absolute; right: 13px; top: 10px; cursor: pointer; z-index: 3;"
                      @click="openDatePicker"
                    />
                  </div>
                </div>
              </div>
              <div class="consult-info-card__right">
                <p class="consult-info-card__description">
                  This represents the date you consulted with your patient and will replace the original evaluation
                  date.
                </p>
              </div>
            </div>
            <div class="scrollable-area">
              <h2 class="content__title">What would you like to do with the Evaluation?</h2>
              <div class="wrapper-protocol">
                <div
                  class="protocol protocol-start"
                  :class="{
                active: form.activeProtocol === 'protocol-start',
                'is-disabled': isTrackProtocolDisabled
              }"
                  @click="handleAction('protocol-start')">
                <span class="protocol__icon">
                  <TrackProtocolIcon/>
                </span>
                  <h3 class="protocol__title">Track Protocol</h3>
                  <p>{{ trackProtocolText }}</p>
                </div>
                <div
                  class="protocol protocol-end-evaluation"
                  :class="{
                active: form.activeProtocol === 'protocol-end-evaluation',
                'highlight-suggested': isEndEvaluationHighlighted
              }"
                  @click="handleAction('protocol-end-evaluation')">
                <span class="protocol__icon">
                  <EndEvaluationIcon/>
                </span>
                  <h3 class="protocol__title">End Evaluation</h3>
                  <p>{{ endEvaluationText }}</p>
                </div>
              </div>
              <div v-if="modalConfirmationEndProtocol.data.addVisit && !isAddNextEvaluationDisabled">
                <div class="custom-checkbox">
                  <v-checkbox
                    color="#008C89"
                    label="Add Next Evaluation Date"
                    @click="toggleNextEvaluation"
                    v-model="form.addNextEvaluation"
                  ></v-checkbox>
                </div>
                <transition name="fade">
                  <div :class="['elements-wrapper', form.addNextEvaluation ? 'visible' : 'hidden']">
                    <div class="elements">
                      <div class="evaluation-date mr-7">
                        <h3 class="elements__title">Evaluation Date</h3>
                        <div class="evaluation-date__wrapper">
                          <datepicker
                            ref="evaluationDatepicker"
                            :show-icon="false"
                            :min="nowDate"
                            v-model="form.evaluationDate"
                            @date:update="form.evaluationDate = $event"
                          />
                          <DateIcon
                            style="position: absolute; right: 13px; top: 10px; cursor: pointer; z-index: 3;"
                            @click="openEvaluationDatePicker"
                          />
                        </div>
                      </div>
                      <div class="evaluation-type">
                        <h3 class="elements__title">Select the Evaluation Type</h3>
                        <div class="elements__survey">
                          <v-checkbox
                            color="#008C89"
                            v-model="form.survey"
                            class="round-checkbox"
                            label="Survey"/>
                          <v-checkbox
                            color="#008C89"
                            v-model="form.nonSurvey"
                            class="round-checkbox"
                            label="Non-Survey"/>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </div>
            </div>
          </div>
        </div>
      </template>
      <template slot="actions">
        <div class="d-flex justify-end align-center pt-5" style="width: 100%">
          <Button height="56"
                  width="100"
                  btn-bg="white"
                  :styles="{ 'color': '#16192C', 'margin-right': '20px', 'border': '1px solid #EDF2F7' }"
                  @click="close">Cancel
          </Button>
          <Button height="56"
                  width="100"
                  btn-bg="dark-green"
                  dark
                  :disabled="isConfirmDisabled"
                  @click="confirm">Confirm
          </Button>
        </div>
      </template>
    </Dialog>
    <confirm v-if="dialogs.confirm.isOpen" @close="close"/>
  </div>
</template>

<script>
import {mapMutations, mapState} from 'vuex'
import Dialog from '@/components/layouts/dialog'
import Button from '@/components/elements/Button'
import confirm from '@/components/layouts/dialogs/confirm-dialog'
import datepicker from '@/components/elements/datepicker'
import Img from '@/components/elements/Img'
import DateIcon from '@/assets/icons/components/date-2.svg'
import TrackProtocolIcon from '@/assets/icons/components/track-protocol.svg'
import EndEvaluationIcon from '@/assets/icons/components/end-evaluation.svg'

const SCENARIOS = {
  NORMAL: 'NORMAL',
  WARNING: 'WARNING',
  ELAPSED: 'ELAPSED'
}

export default {
  name: 'modal-confirmation-end-protocol',
  components: {Dialog, Button, confirm, datepicker, DateIcon, TrackProtocolIcon, EndEvaluationIcon, Img},
  data: () => ({
    form: {
      activeProtocol: null,
      addNextEvaluation: false,
      survey: false,
      nonSurvey: false,
      evaluationDate: null,
      consultDate: null
    },
    showInfoAdjustProtocol: false,
    nowDate: new Date(new Date().setDate(new Date().getDate())).toISOString().slice(0, 10),
  }),
  methods: {
    ...mapMutations('dialogs', ['TOGGLE_DIALOG']),
    close() {
      this.TOGGLE_DIALOG({name: 'modalConfirmationEndProtocol', isOpen: false, root: true})
    },
    confirm() {
      if (!this.isConfirmDisabled) {
        if (this.form.activeProtocol === 'adjust-protocol') {
          this.form.addNextEvaluation = true
          this.form.nonSurvey = true
        }
        this.$emit('request', this.form)
      }
    },
    toggleNextEvaluation() {
      this.form.survey = false
      this.form.nonSurvey = false
    },
    handleAction(action) {
      this.form.activeProtocol = action
      if (action === 'adjust-protocol') {
        this.form.addNextEvaluation = false
      } else if (action === 'end-protocol' || action === 'protocol-end-evaluation') {
        if (action === 'end-protocol') {
          this.form.addNextEvaluation = false
        }
        this.form.survey = false
        this.form.nonSurvey = false
        this.form.evaluationDate = this.modalConfirmationEndProtocol.data.date ?
          new Date(this.modalConfirmationEndProtocol.data.date) :
          null
      }
    },
    onShowInfoAdjustProtocol() {
      this.showInfoAdjustProtocol = true

      this.$nextTick(() => {
        const tooltipContent = document.querySelector('.v-tooltip__content')
        if (tooltipContent) {
          tooltipContent.style.opacity = '1'
        }
      })

      clearTimeout(this.timerId)

      if (this.showInfoAdjustProtocol) {
        this.$nextTick(() => {
          this.timerId = setTimeout(() => {
            this.showInfoAdjustProtocol = false
            this.removeEventListeners()
          }, 10000)
        })
      }

      this.addEventListeners()
    },
    addEventListeners() {
      document.addEventListener('click', this.handleOutsideClick)
      document.addEventListener('keydown', this.handleKeyPress)
    },
    removeEventListeners() {
      document.removeEventListener('click', this.handleOutsideClick)
      document.removeEventListener('keydown', this.handleKeyPress)
    },
    handleOutsideClick(event) {
      const tooltipContent = document.querySelector('.v-tooltip__content')
      if (tooltipContent && !tooltipContent.contains(event.target)) {
        this.showInfoAdjustProtocol = false
        this.removeEventListeners()
      }
    },
    handleKeyPress(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.showInfoAdjustProtocol = false
        this.removeEventListeners()
      }
    },
    openDatePicker() {
      this.$refs.consultDatepicker.menu = true
    },
    openEvaluationDatePicker() {
      this.$refs.evaluationDatepicker.menu = true
    },

    onConsultDateUpdate(newDate) {
      if (!newDate) {
        this.form.consultDate = this.todayDate
        return
      }

      const isFuture = this.$moment(newDate).isAfter(this.$moment(this.todayDate))
      this.form.consultDate = isFuture ? this.todayDate : newDate

      this.$nextTick(() => {
        if (this.uiScenario === SCENARIOS.ELAPSED) {
          this.form.activeProtocol = 'protocol-end-evaluation'
        }
      })
    },
  },
  computed: {
    ...mapState({
      modalConfirmationEndProtocol: state => state.dialogs.modalConfirmationEndProtocol
    }),
    dialogs() {
      return this.$store.state.dialogs
    },
    patient() {
      return this.modalConfirmationEndProtocol.data?.patient || {}
    },
    patientInitials() {
      const first = this.patient.firstName?.[0] || ''
      const last = this.patient.lastName?.[0] || ''
      return (first + last).toUpperCase() || 'P'
    },
    todayDate() {
      return this.$moment().format('YYYY-MM-DD')
    },
    protocolLength() {
      return this.modalConfirmationEndProtocol.data?.supplyDays || 60
    },
    diffDays() {
      if (!this.form.consultDate) return 0
      const start = this.$moment(this.form.consultDate).startOf('day')
      const today = this.$moment().startOf('day')
      return today.diff(start, 'days')
    },
    uiScenario() {
      const N = this.protocolLength
      const diff = this.diffDays

      if (diff >= N) return SCENARIOS.ELAPSED
      if (diff >= (N - 9)) return SCENARIOS.WARNING
      return SCENARIOS.NORMAL
    },
    trackProtocolText() {
      if (this.uiScenario === SCENARIOS.NORMAL) {
        return 'You want to track the patient protocol to allow for visual status updates and reminder emails (optional). The evaluation status will change to In Protocol and the evaluation will be locked from future modifications.'
      }
      if (this.uiScenario === SCENARIOS.WARNING) {
        return 'You want to track the patient protocol to allow for visual status updates. Reminder emails (optional) cannot be sent. The evaluation status will change to Protocol Ending and the evaluation will be locked from future modifications.'
      }
      return 'Based on the entered Date of Patient Consult, the patient protocol cannot be tracked and reminder emails (optional) are disabled.'
    },
    endEvaluationText() {
      if (this.uiScenario === SCENARIOS.NORMAL || this.uiScenario === SCENARIOS.WARNING) {
        return 'You just want to end and close the evaluation. The evaluation status will change to Closed and the evaluation will be locked from future modifications.'
      }
      return 'Based on the entered Date of Patient Consult, the only option is to end and close the evaluation. The evaluation status will change to Closed and the evaluation will be locked from future modifications.'
    },
    isTrackProtocolDisabled() {
      return this.uiScenario === SCENARIOS.ELAPSED
    },
    isEndEvaluationHighlighted() {
      return this.uiScenario === SCENARIOS.ELAPSED
    },
    currentModal() {
      return this.modalConfirmationEndProtocol.title
    },
    isConfirmDisabled() {
      return !this.form.activeProtocol || (this.form.addNextEvaluation && !this.form.survey && !this.form.nonSurvey)
    },
    isAddNextEvaluationDisabled() {
      return (
        this.form.activeProtocol === 'adjust-protocol'
      )
    },
  },
  mounted() {
    const todayTechnical = this.$moment().format('YYYY-MM-DD')
    this.form.consultDate = todayTechnical

    if (this.modalConfirmationEndProtocol.data.date) {
      this.form.evaluationDate = this.$moment(this.modalConfirmationEndProtocol.data.date).format('YYYY-MM-DD')
    } else {
      this.form.evaluationDate = todayTechnical
    }
  },
  watch: {
    'form.survey'(val) {
      if (val) {
        this.form.nonSurvey = false
      }
    },
    'form.nonSurvey'(val) {
      if (val) {
        this.form.survey = false
      }
    },
    showInfoAdjustProtocol(newValue) {
      if (!newValue) {
        this.addEventListeners()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.confirmation-end-protocol {
  ::v-deep .v-dialog {
    //height: auto;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    @media (max-width: 1496px) {
      //height: 100vh;
      max-height: 100vh;
    }

    .v-card {
      //height: auto;
      max-height: 95vh;
      display: flex;
      flex-direction: column;
      overflow: hidden;

      @media (max-width: 1496px) {
        //height: 100vh;
        max-height: 100vh;
      }

      &__title {
        flex: 0 0 auto;
        letter-spacing: 0;
        padding: 36px 36px 25px !important;

        .image {
          position: absolute;
          top: 36px;
          right: 36px;
          cursor: pointer;
        }

        .title {
          color: #27272E;
          font-size: 28px !important;
          font-weight: 600;
          line-height: 2.4rem;
          letter-spacing: 0 !important;
        }
      }

      &__text {
        min-height: 0;
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        overflow: hidden;

        .content {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          min-height: 0;
          //max-height: 100%;
          //height: 100%;
          padding: 0 36px !important;
          overflow: hidden;

          .scrollable-area {
            //display: flex;
            //flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            width: calc(100% + 6px);
            padding-right: 6px;
            box-sizing: border-box;
            overflow-y: auto;

            &::-webkit-scrollbar {
              width: 4px;
            }
            &::-webkit-scrollbar-track {
              background: transparent;
            }
            &::-webkit-scrollbar-thumb {
              background-color: #cbd5e0;
              border-radius: 10px;
              border: none;
            }
          }

          .protocol-adjustment-view {
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;
          }

          .evaluation-finalization-view {
            display: flex;
            flex-direction: column;
            flex: 1;
            min-height: 0;

            .content__title {
              margin-bottom: 55px;
            }
          }

          &__title {
            color: #2D3748;
            font-size: 20px;
            font-weight: 600;
            line-height: 1.4;
            letter-spacing: 0;
            text-align: center;
          }

          &__description {
            color: #718096;
            font-size: 16px;
            font-weight: 400;
            line-height: 1.6;
            letter-spacing: 0;
            text-align: center;
            margin: 2px 0 55px;
            padding-bottom: 17px;
          }

          .wrapper-protocol {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 18px;
            border-bottom: 1px solid #A6B7D4;
            padding-bottom: 33px;
          }

          .protocol {
            position: relative;
            color: #1A202C;
            text-align: center;
            background-color: #EBF2FA;
            border: 2px solid #EBF2FA;
            border-radius: 16px;
            padding: 53px 18px 17px;
            transition: border-color 0.3s ease, box-shadow 0.3s ease;
            cursor: pointer;

            &__icon {
              position: absolute;
              top: -31px;
              left: 50%;
              transform: translateX(-50%);
              width: 62px;
              height: 62px;
              border-radius: 16px;
              background-color: #A6B7D4;
              z-index: 1;
              transition: border-color 0.3s ease;

              svg {
                margin-top: 7px;
                margin-left: 11px;
              }
            }

            &.is-disabled {
              cursor: not-allowed !important;
              pointer-events: none;

              .protocol__icon,
              .protocol__title {
                opacity: 0.5;
              }

              p {
                color: #CF4655;
              }
            }

            &.active,
            &.highlight-suggested {
              border-color: rgba(0, 140, 137, 0.5) !important;
              box-shadow: 0px 0px 20px 0px rgba(0, 140, 137, 0.5);

              .protocol__title {
                color: #008C89;
              }

              .protocol__icon {
                background-color: #40BFB4;
              }
            }

            p {
              font-size: 15px;
              font-weight: 400;
              line-height: 1.5;
              letter-spacing: 0;
              margin: 0;
            }

            &__title {
              font-size: 20px;
              font-weight: 600;
              line-height: 1.4;
              letter-spacing: 0;
              padding-bottom: 15px;
              transition: color 0.3s ease;
            }

            &:hover {
              background-color: rgba(0, 140, 137, 0.1);

              .protocol__icon {
                background-color: #40BFB4;
              }
            }
          }
        }
      }

      &__actions {
        flex: 0 0 auto;
        padding: 0 36px 36px;
      }

      .evaluation-date {
        &__wrapper {
          position: relative;
        }
      }

      .consult-info-card {
        display: flex;
        gap: 30px;
        border-radius: 16px;
        background-color: rgba(208, 220, 255, 0.5);
        padding: 15px 20px;
        margin-bottom: 28px;

        &__left {
          flex: 0 0 auto;
        }

        &__right {
          flex: 1;
          padding-left: 20px;
        }

        &__label {
          color: #2D3748;
          font-size: 16px;
          font-weight: 600;
          line-height: 1.5;
          margin-bottom: 8px;
        }

        &__input-wrapper {
          display: flex;
          align-items: center;
          gap: 13px;
        }

        &__avatar span {
          width: 36px;
          height: 36px;
          background-color: #40BFB4;
          color: #FFFFFF;
          font-size: 16px;
          font-weight: 600;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        &__date {
          position: relative;
        }

        &__description {
          color: #718096;
          font-size: 15px;
          font-weight: 400;
          line-height: 1.4;
          letter-spacing: 0;
          margin: 0;
        }
      }
    }
  }

  .custom-checkbox {
    padding-left: 1px;
    padding-top: 20px;

    ::v-deep .v-input {
      margin: 0;
      padding: 0;

      label {
        color: #2D3748;
        font-size: 16px;
        font-weight: 600;
        line-height: 1.5;
      }

      .v-input--selection-controls__input {
        margin-right: 13px;
        height: 16px;
        width: 16px;

        &:hover .v-input--selection-controls__ripple:before {
          opacity: 0;
        }

        .v-icon {
          color: #C9CED6;
        }

        .v-input--selection-controls__ripple {
          height: 16px;
          width: 16px;
          left: 0;
          top: 0;
          border-radius: 0;
          margin: 0;
        }
      }

      .v-messages {
        display: none;
      }
    }
  }

  .elements-wrapper {
    margin-top: 16px;
    transition: opacity 0.3s ease;

    &.hidden {
      opacity: 0;
    }

    &.visible {
      opacity: 1;
    }
  }

  .elements {
    display: flex;
    justify-content: flex-start;
    border-radius: 16px;
    background-color: #F7FAFC;
    border: 1px solid #E4ECF7;
    padding: 22px 20px 26px;

    &__title {
      color: #2D3748;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0;
      margin-bottom: 10px;
    }

    &__survey {
      display: flex;
      align-items: center;
      margin-left: -10px;
      margin-top: -3px;

    }

    .round-checkbox {
      margin: 0;

      &::v-deep.v-input {
        background-color: transparent;
        border-radius: 8px;
        padding: 10px;
        margin: 0;

        label {
          color: #2D3748;
          font-size: 14px;
          font-weight: 400;
          line-height: 23px;
          margin-left: 0px;
        }

        .v-input--selection-controls__input {
          display: flex;
          justify-content: center;
          align-items: center;

          .v-icon {
            display: none;
          }
        }

        .v-input__control {
          .v-messages {
            display: none;
          }
        }

        .v-input--selection-controls__ripple {
          width: 20px;
          height: 20px;
          left: 0;
          top: 2px;
          border-radius: 50%;
          border: 2px solid #C9CED6;
          margin: 0;

          &::before {
            content: "";
            width: 10px;
            height: 10px;
            top: 3px;
            left: 3px;
            border-radius: 50%;
            background-color: transparent;
            transition: background-color 0.2s ease;
            opacity: 1;
            transform: scale(1);
          }
        }

        &.v-input--is-dirty {
          background-color: #F7FAFC;

          .v-input--selection-controls__ripple {
            border: 2px solid #008C89;

            &::before {
              background-color: #008C89;
            }
          }
        }
      }
    }
  }

  ::v-deep .v-btn {
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0;
    border-radius: 16px !important;

    &.v-btn--disabled {
      color: #A6B7D4 !important;
      background-color: #EBF2FA !important;
    }
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.v-tooltip {
  display: inline-block;

  .v-tooltip__content {
    position: absolute;
    top: calc(100% - 35px) !important;
    left: 135px !important;

    .custom-tooltip {
      width: 350px;
      background-color: #FFFFFF;
      box-shadow: 0 0 15px 0 rgba(113, 128, 150, 0.24);
      border-radius: 16px;
      color: #2D3748;
      font-size: 16px;
      font-weight: 400;
      letter-spacing: 0;
      padding: 20px;
      margin: -8px  -20px;

      p {
        margin: 0;
      }
    }
  }
}
</style>

<style lang="scss">
.confirmation-end-protocol {
  .datepicker {
    width: 140px;
    height: 36px;

    &.consult-datepicker-white .input-with-icon {
      background-color: #FFFFFF;
    }

    .input-with-icon {
      height: 100%;
      background-color: #EDF2F7;
      border: 1px solid #E4ECF7;
      border-radius: 8px;
      padding: 0 40px 0 12px !important;

      .input .v-input__control .v-input__slot .v-text-field__slot input {
        color: #1A202C !important;
        font-weight: 500;
        font-size: 14px;
      }
    }
  }
}
</style>
